import { ComboDetailPayload } from '@teko/discovery';
import { Card, Glide } from '@teko/ds-desktop';
import { BlockHeader } from '@teko/teko-ecommerce';
import { getTrackingData } from '@teko/tracking';
import { convertProductDetail } from 'helpers';
import { useCartCombo, useComboList, useComboTitle } from 'hooks';
import React, { useState } from 'react';
import { ComboItem as IComboItem } from 'types';
import { ComboItem } from './ComboItem';
import { DetailComboModal } from './DetailComboModal';

interface ComboCarouselProps {
  mainSku: string;
}

export const ComboCarousel: React.FC<ComboCarouselProps> = ({
  mainSku,
}: ComboCarouselProps) => {
  const [visibleDetail, setVisibleDetail] = useState(false);
  const [activeCombo, setActiveCombo] = useState<
    ComboDetailPayload & {
      type: IComboItem['type'];
    }
  >();
  const { comboList } = useComboList(mainSku);
  const { onAddComboToCart } = useCartCombo(mainSku);
  const { title, onSlideChange } = useComboTitle(comboList);
  const handleAddToCart = (comboItem: IComboItem) => {
    const payload = {
      promotionId: comboItem.promotionId,
      comboLineItems: comboItem.items.map((el) => {
        const convertedProduct = convertProductDetail(el.product);
        return {
          sku: convertedProduct.sku,
          quantity: el.quantity,
          selectPromotionId: convertedProduct.selectPromotionId,
        };
      }),
    };
    onAddComboToCart?.(comboItem.type, payload, false);
  };
  const handleViewDetailCombo = (comboItem: IComboItem) => {
    const comboLineItems = comboItem.items.map((el) => {
      const convertedProduct = convertProductDetail(el.product);
      return {
        sku: convertedProduct.sku,
        quantity: el.quantity,
        selectPromotionId: convertedProduct.selectPromotionId,
      };
    });
    setActiveCombo({
      type: comboItem.type,
      comboLineItems,
      promotionId: comboItem.promotionId,
    });
    setVisibleDetail(true);
  };
  if (comboList.length === 0) return null;
  return (
    <Card
      marginBottom="s24"
      header={
        <BlockHeader
          title={title || ''}
          link={
            comboList.length > 1
              ? {
                  color: 'primary500',
                  href: `/products/${mainSku}/combos`,
                }
              : undefined
          }
          tracking={getTrackingData({
            isTrackContent: true,
            regionName: 'productComboCarousel',
            contentName: 'viewAllCombo',
            target: 'comboList',
          })}
        />
      }
    >
      {comboList.length === 1 ? (
        <ComboItem
          comboItem={comboList[0]}
          onAddToCart={() => handleAddToCart(comboList[0])}
          onViewDetail={() => handleViewDetailCombo(comboList[0])}
          {...getTrackingData({
            isTrackContent: true,
            regionName: 'productComboList',
            contentName: 'productComboItem',
            index: 1,
          })}
        />
      ) : (
        <Glide perView={1} onSlideChange={onSlideChange}>
          {comboList.map((el, index) => (
            <ComboItem
              key={index}
              comboItem={el}
              onAddToCart={() => handleAddToCart(el)}
              onViewDetail={() => handleViewDetailCombo(el)}
              {...getTrackingData({
                isTrackContent: true,
                regionName: 'productComboList',
                contentName: 'productComboItem',
                index,
              })}
            />
          ))}
        </Glide>
      )}
      {activeCombo && (
        <DetailComboModal
          visible={visibleDetail}
          onCloseModal={() => {
            setVisibleDetail(false);
            setActiveCombo(undefined);
          }}
          onAddToCart={async (type, payload) => {
            await onAddComboToCart?.(type, payload, false);
            setVisibleDetail(false);
            setActiveCombo(undefined);
          }}
          {...activeCombo}
        />
      )}
    </Card>
  );
};
